import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import clsx from 'clsx';
import React, { forwardRef, useCallback } from 'react';
import mergeRefs from 'react-merge-refs';
import { ClearButton } from '../input/ClearButton.js';
import defaultStyles from '../input/styles/Input.module.scss.js';
import { useTaggedInput } from './hooks/useInputTag.js';
import tagStyles from './styles/InputTag.module.scss.js';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { getInputClasses } from '../input/utils/input-helper.js';
import { Tag } from '../tag/Tag.js';
import { InputBase } from '../input/InputBase.js';

const keepFocus = e => {
  e.target.tagName !== 'INPUT' && e.preventDefault();
};
const ID_SUFFIX = '_input_tag';
const InputTag = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    placeholder,
    theme,
    suffix,
    disabled,
    readOnly,
    allowClear,
    value: taggedValue,
    inputValue: inputValueProp,
    defaultValue,
    complexValue,
    disableInput,
    name,
    renderTag,
    onInputChange,
    onKeyDown,
    onPaste,
    onChange,
    onFocus,
    onBlur,
    onPressEnter,
    onRemove,
    onClear,
    onClick,
    validationStatus,
    clearIcon,
    autoFocus,
    saveOnBlur,
    size = 'md',
    variant = 'default',
    dataTestId,
    idSuffix = ID_SUFFIX
  } = props;
  const styles = composeThemeFromProps({
    ...defaultStyles,
    ...tagStyles
  }, {
    theme
  }, {
    compose: Compose.Merge
  });
  const {
    focus,
    setFocus,
    inputRef,
    refDelay,
    value,
    valueChangeHandler,
    tagCloseHandler,
    hotkeyHandler,
    tryAddInputValueToTag,
    inputValue,
    setInputValue
  } = useTaggedInput({
    onChange,
    taggedValue,
    inputValueProp,
    defaultValue,
    onRemove,
    complexValue
  });
  const notEmpty = allowClear && !disabled && value.length;
  const hasSuffix = suffix || notEmpty;
  const {
    inputClassNames,
    innerClassNames
  } = getInputClasses({
    clsx,
    styles,
    size,
    variant,
    suffix: hasSuffix,
    disabled,
    validationStatus,
    prefix: null,
    allowClear,
    value: value || inputValue,
    focus,
    withPrefixes: null,
    hasClear: notEmpty,
    withLabel: false
  });
  const clearButton = notEmpty ? /*#__PURE__*/jsx(ClearButton, {
    theme: {
      root: styles.clear
    },
    clearIcon: clearIcon,
    onClick: e => {
      e.stopPropagation();
      valueChangeHandler([], e);
      if (!focus) {
        inputRef.current && inputRef.current.focus();
      }
      onClear && onClear();
    }
  }) : null;
  const mergedRenderTag = useCallback((item, index, isRepeat) => {
    const _tags = taggedValue || defaultValue;
    const tagItem = complexValue ? _tags.find(i => i.value === item) : {
      value: item,
      label: item
    };
    const closable = !readOnly && !disabled;
    const onClose = event => {
      tagCloseHandler(tagItem, index, event);
    };
    if (renderTag) {
      return renderTag({
        value: tagItem.value,
        label: tagItem.label,
        closable,
        onClose
      }, index, value);
    }
    const repeatKey = isRepeat ? `${item}-${index}` : item;
    return /*#__PURE__*/jsx(Tag, {
      visible: true,
      onClose: onClose,
      closable: !readOnly && !disabled,
      theme: {
        tag: styles.tag
      },
      children: /*#__PURE__*/jsx("span", {
        title: typeof tagItem.label === 'string' ? tagItem.label : undefined,
        className: styles.tag_content,
        children: typeof tagItem.label === 'string' ? tagItem.label.replace(/\s/g, '\u00A0') : tagItem.label
      })
    }, typeof item === 'object' ? index : repeatKey);
  }, [complexValue, defaultValue, disabled, readOnly, renderTag, styles, tagCloseHandler, taggedValue, value]);
  const renderTags = value && value.map((x, i) => {
    // Check whether two tags have same value. If so, set different key for them to avoid only rendering one tag.
    const isRepeat = value.findIndex(item => item === x) !== i;
    const eleTag = mergedRenderTag(x, i, isRepeat);
    return !isRepeat && /*#__PURE__*/React.isValidElement(eleTag) ? eleTag : null;
  });
  const inputElement = /*#__PURE__*/jsx(InputBase, {
    name: name,
    ref: mergeRefs([inputRef, ref]),
    className: clsx(inputClassNames, styles.tag_input),
    autoComplete: "off",
    disabled: disabled || disableInput,
    readOnly: readOnly,
    autoFocus: autoFocus,
    placeholder: !value.length ? placeholder : '',
    id: `${name}${idSuffix}`,
    autoFitWidth: {
      delay: () => refDelay.current
    },
    onPressEnter: async e => {
      inputValue && e.preventDefault();
      onPressEnter && onPressEnter(e);
      await tryAddInputValueToTag(e);
    },
    onFocus: e => {
      if (!disabled && !readOnly) {
        setFocus(true);
        onFocus && onFocus(e);
      }
    },
    onBlur: async e => {
      setFocus(false);
      onBlur && onBlur(e);
      if (saveOnBlur) {
        await tryAddInputValueToTag(e);
      }
      setInputValue('');
    },
    value: inputValue,
    onValueChange: (v, e) => {
      setInputValue(v);
      // Only fire callback on user input to ensure parent component can get real input value on controlled mode.
      onInputChange && onInputChange(v, e);
    },
    onKeyDown: event => {
      hotkeyHandler(event);
      onKeyDown && onKeyDown(event);
    },
    onPaste: onPaste
  });
  return /*#__PURE__*/jsx(Fragment, {
    children: /*#__PURE__*/jsxs("div", {
      className: innerClassNames,
      role: "presentation",
      onMouseDown: e => {
        focus && keepFocus(e);
      },
      onClick: e => {
        !focus && inputRef.current && inputRef.current.focus();
        if (onClick) {
          onClick(e);
        }
      },
      onKeyDown: e => {
        focus && keepFocus(e);
      },
      onFocus: () => {
        inputRef?.current?.focus();
      },
      onBlur: () => {
        inputRef?.current?.blur();
      },
      "data-test-id": dataTestId,
      children: [/*#__PURE__*/jsxs("div", {
        className: styles.tag_wrapper,
        children: [renderTags, inputElement]
      }), clearButton]
    })
  });
});
InputTag.displayName = 'InputTag';

export { InputTag };
